import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { trackRegistrationGenderOnce } from '../lib/metrikaAnalytics';
import { RUSSIAN_CITIES } from '../data/russianCities';

const CompleteProfile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [formData, setFormData] = useState({
    age: '',
    height: '',
    weight: '',
    gender: '',
    education: '',
    smoking: '',
    city: '',
    description: ''
  });

  useEffect(() => {
    if (user?.profile_completed) {
      navigate('/videochat');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.age) {
      setFormData(prev => ({ ...prev, age: String(user.age) }));
    }
  }, [user?.age]);

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!citySearch) return RUSSIAN_CITIES.slice(0, 20);
    return RUSSIAN_CITIES.filter(city =>
      city.toLowerCase().includes(citySearch.toLowerCase())
    ).slice(0, 20);
  }, [citySearch]);

  const handleCitySelect = (city) => {
    setFormData({ ...formData, city });
    setCitySearch(city);
    setShowCityDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const age = Number(formData.age);
    const height = Number(formData.height);
    const weight = Number(formData.weight);
    const hasRequiredSelections = formData.city && formData.gender && formData.education && formData.smoking;
    const hasValidNumbers = age >= 18 && age <= 120 && height >= 100 && height <= 250 && weight >= 30 && weight <= 300;

    if (!hasRequiredSelections || !hasValidNumbers) {
      toast.error('Заполните все обязательные поля профиля');
      return;
    }

    setLoading(true);

    try {
      const response = await api.put('/profile', {
        age,
        height,
        weight,
        gender: formData.gender,
        education: formData.education,
        smoking: formData.smoking,
        city: formData.city,
        description: formData.description || undefined
      });

      if (!response.data?.profile_completed) {
        toast.error('Заполните все обязательные поля профиля');
        return;
      }

      if (!user?.profile_completed) {
        trackRegistrationGenderOnce({
          gender: response.data?.gender || formData.gender,
          userId: response.data?.id || user?.id,
        });
      }

      updateUser(response.data);
      toast.success('Профиль заполнен!');
      navigate('/filters');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения профиля');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-[#1F1F1F] text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Заполните профиль
        </h1>
        <p className="text-center text-[#7A7A7A] mb-8">
          Это поможет найти лучшие совпадения
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="age">Возраст *</Label>
              <Input
                id="age"
                type="number"
                min="18"
                max="120"
                required
                value={formData.age}
                onChange={(e) => setFormData({...formData, age: e.target.value})}
                data-testid="profile-age-input"
              />
            </div>

            <div className="relative">
              <Label htmlFor="city">Ближайший город *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#7A7A7A] z-10" />
                <Input
                  id="city"
                  type="text"
                  placeholder="Начните вводить название..."
                  value={citySearch}
                  required
                  aria-required="true"
                  onChange={(e) => {
                    setCitySearch(e.target.value);
                    setShowCityDropdown(true);
                    if (!e.target.value) {
                      setFormData({ ...formData, city: '' });
                    }
                  }}
                  onFocus={() => setShowCityDropdown(true)}
                  className="pl-10"
                  data-testid="profile-city-input"
                />
              </div>

              {/* City dropdown */}
              {showCityDropdown && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-[#E5E5E5] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredCities.length === 0 ? (
                    <div className="p-3 text-center text-[#7A7A7A] text-sm">
                      Город не найден
                    </div>
                  ) : (
                    filteredCities.map((city) => (
                      <button
                        key={city}
                        type="button"
                        onClick={() => handleCitySelect(city)}
                        className={`w-full text-left px-4 py-2 hover:bg-[#F6F7F9] transition-colors ${
                          formData.city === city ? 'bg-[#1A73E8]/10 text-[#1A73E8]' : 'text-[#1F1F1F]'
                        }`}
                      >
                        {city}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="height">Рост (см) *</Label>
              <Input
                id="height"
                type="number"
                min="100"
                max="250"
                required
                value={formData.height}
                onChange={(e) => setFormData({...formData, height: e.target.value})}
                data-testid="profile-height-input"
              />
            </div>

            <div>
              <Label htmlFor="weight">Вес (кг) *</Label>
              <Input
                id="weight"
                type="number"
                min="30"
                max="300"
                required
                value={formData.weight}
                onChange={(e) => setFormData({...formData, weight: e.target.value})}
                data-testid="profile-weight-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="gender">Пол *</Label>
            <Select value={formData.gender} onValueChange={(value) => setFormData({...formData, gender: value})} required>
              <SelectTrigger data-testid="profile-gender-select">
                <SelectValue placeholder="Выберите пол" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Мужской</SelectItem>
                <SelectItem value="female">Женский</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="education">Образование *</Label>
            <Select value={formData.education} onValueChange={(value) => setFormData({...formData, education: value})} required>
              <SelectTrigger data-testid="profile-education-select">
                <SelectValue placeholder="Выберите образование" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="higher">Высшее</SelectItem>
                <SelectItem value="secondary">Среднее</SelectItem>
                <SelectItem value="vocational">Средне-специальное</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="smoking">Отношение к курению *</Label>
            <Select value={formData.smoking} onValueChange={(value) => setFormData({...formData, smoking: value})} required>
              <SelectTrigger data-testid="profile-smoking-select">
                <SelectValue placeholder="Выберите отношение" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Неважно</SelectItem>
                <SelectItem value="negative">Отрицательно</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="description">О себе (необязательно)</Label>
            <Textarea
              id="description"
              placeholder="Расскажите немного о себе..."
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={4}
              data-testid="profile-description-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full py-6 rounded-full text-white font-semibold text-lg"
            style={{ background: 'linear-gradient(135deg, #1A73E8 0%, #6A9EFF 100%)' }}
            data-testid="save-profile-button"
          >
            {loading ? 'Сохранение...' : 'ПРОДОЛЖИТЬ'}
          </Button>
        </form>
      </div>

      {/* Click outside to close city dropdown */}
      {showCityDropdown && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowCityDropdown(false)}
        />
      )}
    </div>
  );
};

export default CompleteProfile;
